import { useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import whitelist from '../config/whitelist.json';
import { AZURE_AD_CONFIG } from '../config/azureAdConfig';
import { AuthorizationResult, AuthorizationContext, ExtendedIdTokenClaims, GroupMembership } from '../types/auth';
import { GraphGroupService } from '../services/graphGroupService';
import { userDbApi } from '../services/userDbApi';

/**
 * Enhanced authorization hook that supports both Azure AD Groups and Email Whitelist
 * UPDATED: Now with automatic Graph API fallback for group overage (>200 groups)
 */
export const useAuthorization = (): AuthorizationContext => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  /**
   * LEGACY: Extract group membership from ID token claims
   * NO LONGER USED - Database whitelist is the only authorization method
   * Kept for historical reference only
   */
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const getGroupMembership = useCallback(async (): Promise<GroupMembership> => {
    const idTokenClaims = account?.idTokenClaims as ExtendedIdTokenClaims;
    
    if (!idTokenClaims) {
      return {
        fromIdToken: [],
        hasOverage: false,
        requiresGraphCall: false,
        source: 'id_token'
      };
    }

    // Use the new Graph API service to handle overage automatically
    try {
      const result = await GraphGroupService.getGroupsWithFallback(
        instance,
        account!,
        idTokenClaims,
        AZURE_AD_CONFIG.GROUPS_CLAIM_NAME
      );

      if (AZURE_AD_CONFIG.ENABLE_AUTH_LOGGING) {
        console.log('🔍 Group membership analysis:', {
          groupsFound: result.groups.length,
          source: result.source,
          hadOverage: result.hadOverage,
          totalGroupsRetrieved: result.groups.length
        });
      }

      return {
        fromIdToken: result.source === 'id_token' ? result.groups : [],
        fromGraphApi: result.source === 'graph_api' ? result.groups : undefined,
        hasOverage: result.hadOverage,
        requiresGraphCall: result.hadOverage && result.source === 'graph_api',
        source: result.source
      };
    } catch (error) {
      console.error('❌ Failed to retrieve group membership:', error);
      
      // Fallback: try to get groups from token directly (old behavior)
      const groupsFromToken = idTokenClaims[AZURE_AD_CONFIG.GROUPS_CLAIM_NAME] || [];
      const hasOverage = GraphGroupService.hasGroupOverage(idTokenClaims);
      
      return {
        fromIdToken: Array.isArray(groupsFromToken) ? groupsFromToken : [],
        hasOverage,
        requiresGraphCall: hasOverage,
        source: 'id_token',
        graphApiFailed: true,
        graphApiError: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }, [account, instance]);
  /* eslint-enable @typescript-eslint/no-unused-vars */

  /**
   * Email-based authorization check (legacy/fallback)
   */
  const checkEmail = useMemo(() => {
    return (email: string): boolean => {
      if (!email) return false;
      
      const emailLower = email.toLowerCase();
      
      // Check JSON whitelist first
      const jsonWhitelist = whitelist.authorizedEmails.map(e => e.toLowerCase());
      if (jsonWhitelist.includes(emailLower)) {
        if (AZURE_AD_CONFIG.ENABLE_AUTH_LOGGING) {
          console.log('✅ Email authorized via JSON whitelist:', email);
        }
        return true;
      }
      
      // Fallback to environment variable whitelist
      const envWhitelist = process.env.REACT_APP_AUTHORIZED_EMAILS?.split(',').map(e => e.trim().toLowerCase()) || [];
      if (envWhitelist.length > 0 && envWhitelist.includes(emailLower)) {
        if (AZURE_AD_CONFIG.ENABLE_AUTH_LOGGING) {
          console.log('✅ Email authorized via environment variable:', email);
        }
        return true;
      }
      
      return false;
    };
  }, []);

  /**
   * Get comprehensive authorization result
   * UPDATED: Now async to support Graph API fallback for group overage
   * UPDATED: Now checks database whitelist after JSON whitelist (but before Azure AD groups)
   * 
   * Authorization priority order:
   * 1. JSON whitelist (failsafe - no DB dependency)
   * 2. Database whitelist (dynamic authorization)
   * 3. Azure AD groups (if enabled)
   * 4. Deny access
   */
  const getAuthorizationResult = useCallback(async (): Promise<AuthorizationResult> => {
    const userEmail = (account?.username || account?.idTokenClaims?.email || account?.idTokenClaims?.preferred_username) as string | undefined;
    
    // PRIORITY 1: Check JSON whitelist FIRST (failsafe - no DB dependency)
    // This avoids unnecessary API calls for whitelisted users
    if (userEmail && checkEmail(userEmail)) {
      if (AZURE_AD_CONFIG.ENABLE_AUTH_LOGGING) {
        console.log('✅ User authorized via JSON whitelist (failsafe)');
      }
      
      return {
        isAuthorized: true,
        authorizationMethod: 'email',
        authorizedGroups: [AZURE_AD_CONFIG.AUTHORIZED_GROUP_ID],
        userEmail,
        groupCheckPerformed: false,
        emailCheckPerformed: true
      };
    }

    // PRIORITY 2: Check database whitelist (dynamic authorization)
    // CRITICAL: Database is the source of truth - no Azure AD fallback
    if (userEmail) {
      try {
        const dbAuthResult = await userDbApi.checkAuthorization(userEmail);
        
        if (dbAuthResult.authorized) {
          if (AZURE_AD_CONFIG.ENABLE_AUTH_LOGGING) {
            console.log('✅ User authorized via database whitelist', {
              method: dbAuthResult.method,
              source: dbAuthResult.source
            });
          }
          
          return {
            isAuthorized: true,
            authorizationMethod: 'database',
            authorizedGroups: [AZURE_AD_CONFIG.AUTHORIZED_GROUP_ID],
            userEmail,
            groupCheckPerformed: false,
            emailCheckPerformed: true,
            databaseCheckPerformed: true
          };
        } else {
          // User NOT in database whitelist - DENY ACCESS (no Azure AD fallback)
          if (AZURE_AD_CONFIG.ENABLE_AUTH_LOGGING) {
            console.log('❌ User NOT in database whitelist - access DENIED', {
              email: userEmail,
              dbResult: dbAuthResult
            });
          }
          
          return {
            isAuthorized: false,
            authorizationMethod: 'none',
            authorizedGroups: [AZURE_AD_CONFIG.AUTHORIZED_GROUP_ID],
            userEmail,
            groupCheckPerformed: false,
            emailCheckPerformed: true,
            databaseCheckPerformed: true,
            errorMessage: `User ${userEmail} is not in the authorized database whitelist`
          };
        }
      } catch (error) {
        // Database check failed - DENY ACCESS (no Azure AD fallback)
        console.error('❌ Database whitelist check failed - access DENIED:', error);
        
        return {
          isAuthorized: false,
          authorizationMethod: 'none',
          authorizedGroups: [AZURE_AD_CONFIG.AUTHORIZED_GROUP_ID],
          userEmail,
          groupCheckPerformed: false,
          emailCheckPerformed: true,
          databaseCheckPerformed: false,
          errorMessage: `Database authorization check failed: ${error instanceof Error ? error.message : 'Unknown error'}`
        };
      }
    }

    // LEGACY AZURE AD GROUP-BASED AUTH CODE - NO LONGER USED
    // Database whitelist is now the ONLY source of truth (after JSON whitelist)
    // This code is kept for historical reference but is unreachable
    /*
    if (AZURE_AD_CONFIG.USE_GROUP_BASED_AUTH) {
      try {
        const membership = await getGroupMembership();
        const allGroups = membership.fromGraphApi || membership.fromIdToken;
        const isInGroup = allGroups.includes(AZURE_AD_CONFIG.AUTHORIZED_GROUP_ID);
        
        if (isInGroup) {
          return { isAuthorized: true, authorizationMethod: 'group', ... };
        }
      } catch (error) {
        console.error('❌ Group check failed:', error);
      }
    }
    */
    
    // This should never be reached - database check always returns above
    // Keeping as final safety fallback
    return {
      isAuthorized: false,
      authorizationMethod: 'none',
      authorizedGroups: [AZURE_AD_CONFIG.AUTHORIZED_GROUP_ID],
      userEmail,
      groupCheckPerformed: false,
      emailCheckPerformed: true,
      errorMessage: `Authorization check failed - no email provided`
    };
  }, [account, checkEmail]); // Removed getGroupMembership - no longer used

  // Extract current user info
  const userEmail = (account?.username || account?.idTokenClaims?.email || account?.idTokenClaims?.preferred_username) as string | undefined;

  // Return simplified sync interface for backward compatibility
  // The actual authorization check (getAuthorizationResult) is async
  return useMemo(() => ({
    userEmail,
    userGroups: [], // Will be populated by getAuthorizationResult
    hasGroupsOverage: false, // Will be determined in getAuthorizationResult
    checkEmail,
    checkGroup: () => false, // Deprecated - use getAuthorizationResult instead
    getAuthorizationResult // Now async
  }), [userEmail, checkEmail, getAuthorizationResult]);
};

export default useAuthorization;
